import type { CrowdExit } from './data';
import { SYMBOL, num } from './format';

/**
 * The "If you’d put in" slider. Its position runs 0–STEPS and maps
 * logarithmically onto 10–10,000, so 10, 100 and 1,000 sit evenly along it,
 * then snaps to a round number: whole units under 100, tens under 1,000 and
 * hundreds above.
 */
export const STAKE_MIN = 10;
export const STAKE_MAX = 10_000;
export const STEPS = 1000;

const SPAN = Math.log10(STAKE_MAX / STAKE_MIN);

export function snap(value: number): number {
  const v = Math.min(STAKE_MAX, Math.max(STAKE_MIN, value));
  if (v < 100) return Math.round(v);
  if (v < 1000) return Math.round(v / 10) * 10;
  return Math.round(v / 100) * 100;
}

export function stakeAt(position: number): number {
  const p = Math.min(STEPS, Math.max(0, position)) / STEPS;
  return snap(STAKE_MIN * 10 ** (p * SPAN));
}

export function positionOf(stake: number): number {
  const v = Math.min(STAKE_MAX, Math.max(STAKE_MIN, stake));
  return Math.round((Math.log10(v / STAKE_MIN) / SPAN) * STEPS);
}

/**
 * Bar length on a log scale, 0–1: Revolut's +40,000% is the full bar, so
 * +100% still shows rather than vanishing next to it.
 */
export function barLength(ret: number | null): number {
  if (ret === null) return 0;
  return Math.min(1, Math.max(0, Math.log10(1 + ret / 100) / Math.log10(401)));
}

/** "£100 → £40,100" as its two halves, or null where no return was disclosed. */
export function stakeResult(
  exit: CrowdExit,
  stake: number,
): { from: string; to: string } | null {
  if (exit.ret === null) return null;
  const sym = SYMBOL[exit.currency];
  return {
    from: `${sym}${num(stake)}`,
    to: `${sym}${num(stake * (1 + exit.ret / 100))}`,
  };
}
